/**
 * First-time visitor session: localStorage lifetime flag + session visitId + in-memory trail.
 */

export const FIRST_VISIT_FLAG_KEY = 'm2b_has_visited';
export const VISIT_ID_KEY = 'm2b_visit_id';
export const PATH_TRAIL_MAX_STEPS = 20;
export const PATH_TRAIL_MAX_CHARS = 400;

let pathTrail = [];
let lastAction = null;
let exitFlushedForVisitId = null;

function safeStorage(getStore) {
  try {
    return getStore();
  } catch {
    return null;
  }
}

function randomId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `v_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * @returns {boolean}
 */
export function isFirstVisitBrowser() {
  const ls = safeStorage(() => window.localStorage);
  if (!ls) return false;
  return !ls.getItem(FIRST_VISIT_FLAG_KEY);
}

/**
 * @returns {string|null}
 */
export function getOrCreateVisitId() {
  const ss = safeStorage(() => window.sessionStorage);
  if (!ss) return null;
  let id = ss.getItem(VISIT_ID_KEY);
  if (!id) {
    id = randomId();
    ss.setItem(VISIT_ID_KEY, id);
  }
  return id;
}

/**
 * Mark browser as having completed a first visit (no longer first-time).
 */
export function markHasVisited() {
  const ls = safeStorage(() => window.localStorage);
  if (!ls) return;
  try {
    ls.setItem(FIRST_VISIT_FLAG_KEY, '1');
  } catch {
    /* ignore quota */
  }
}

/**
 * @param {string} pageKey
 */
export function appendPathTrail(pageKey) {
  const key = String(pageKey || '').trim();
  if (!key) return;
  if (pathTrail[pathTrail.length - 1] === key) return;
  pathTrail = [...pathTrail, key].slice(-PATH_TRAIL_MAX_STEPS);
}

/**
 * @returns {string[]}
 */
export function getPathTrail() {
  return [...pathTrail];
}

/**
 * @returns {string}
 */
export function formatPathTrail(trail = pathTrail) {
  const joined = (trail || []).join(' > ');
  return joined.slice(0, PATH_TRAIL_MAX_CHARS);
}

/**
 * @param {{ name: string, pageKey?: string|null }} action
 */
export function setLastAction(action) {
  if (!action?.name) return;
  lastAction = {
    name: String(action.name).slice(0, 64),
    pageKey: action.pageKey ? String(action.pageKey).slice(0, 120) : null,
    at: Date.now(),
  };
}

/**
 * @returns {{ name: string, pageKey: string|null, at: number }|null}
 */
export function getLastAction() {
  return lastAction ? { ...lastAction } : null;
}

/**
 * @param {string|null} visitId
 * @returns {boolean} true if this visit has not flushed exit yet
 */
export function canFlushFirstVisitExit(visitId) {
  if (!visitId) return false;
  return exitFlushedForVisitId !== visitId;
}

/**
 * @param {string|null} visitId
 */
export function markFirstVisitExitFlushed(visitId) {
  if (visitId) exitFlushedForVisitId = visitId;
}

/** Test-only reset */
export function __resetFirstVisitStateForTests() {
  pathTrail = [];
  lastAction = null;
  exitFlushedForVisitId = null;
}
